<template>
  <v-container v-if="$can('browse', 'YachtEquipmentPack')">
    <v-row
      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Yacht Equipment Packs
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="$can('create', 'YachtEquipmentPack')"
          color="grey darken-2"
          class="mr-2"
          text
          @click="massImportDialog = true"
        >
          <v-icon left>
            upload
          </v-icon>
          Mass Import
        </v-btn>
        <v-btn
          v-if="$can('create', 'YachtEquipmentPack')"
          color="primary"
          class="ma-0"
          @click="openYachtEquipmentPackEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader>
            Installed By
          </v-subheader>
          <v-list
            color="transparent"
            dense
          >
            <v-list-item-group
              v-model="filtersDirty.type"
              mandatory
              color="primary"
            >
              <v-list-item
                v-for="installedBy in allInstalledBys"
                :key="installedBy.value"
                class="rounded"
                :value="installedBy.value"
                active-class="primary white--text font-weight-bold elevation-10"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ installedBy.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <v-subheader>
            Status
          </v-subheader>

          <v-list
            dense
            color="transparent"
          >
            <v-list-item-group
              v-model="filtersDirty.status"
              mandatory
              color="primary"
            >
              <v-list-item
                v-for="status in allStatuses"
                :key="status.value"
                class="rounded"
                :value="status.value"
                active-class="primary white--text font-weight-bold elevation-10"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ status.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <v-autocomplete
            v-model="filtersDirty.yacht_equipment_pack_category_id"
            solo
            label="Category"
            :items="categories.data"
            :loading="categories.loading"
            item-text="name"
            item-value="id"
            hide-details
            clearable
            dense
            class="mb-2"
          >
            <template v-slot:item="{ item: category }">
              <v-avatar
                size="18px"
                class="mr-2"
                :color="category.color"
              />
              {{ category.name }}
            </template>
          </v-autocomplete>
          <yacht-model-select
            v-model="filtersDirty.yacht_model_id"
            solo
            label="Yacht Model"
            :items="yachtModels.data"
            :loading="yachtModels.loading"
            item-text="label"
            item-value="id"
            hide-details
            clearable
            dense
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.country_id"
            solo
            label="Country"
            :items="countries.data"
            :loading="countries.loading"
            item-text="name"
            item-value="id"
            autocomplete="off"
            hide-details
            clearable
            dense
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.supplier_id"
            solo
            label="Supplier"
            :items="contractors.data"
            :loading="contractors.loading"
            item-text="name"
            item-value="id"
            hide-details
            clearable
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.label"
            solo
            label="Label"
            hide-details
            clearable
            dense
            autocomplete="no"
          />
        </div>
      </v-col>
      <v-col
        cols="13"
      >
        <div>
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtEquipmentPacks.meta.from"
            :to="yachtEquipmentPacks.meta.to"
            :total="yachtEquipmentPacks.meta.total"
            :last-page="yachtEquipmentPacks.meta.last_page"
            :page.sync="pagination.page"
            class="mb-1 align-center"
            :has-actions="selected.length > 0"
          >
            <template
              v-if="selected.length > 0"
              v-slot:action
            >
              <v-row
                dense
                class="caption"
                align="center"
              >
                <v-col
                  cols="auto"
                  style="min-width: 130px;"
                >
                  <span>With <strong>{{ selected.length }} selected</strong></span>
                </v-col>
                <v-col
                  cols="auto"
                >
                  <v-btn
                    outlined
                    class="text-none"
                    small
                    color="primary"
                    @click="massEditDialog = true"
                  >
                    <v-icon
                      small
                      left
                    >
                      edit
                    </v-icon>
                    Mass Edit
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </pagination>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Data">-->
          <v-data-table
            v-model="selected"
            :headers="tableHeaders"
            :items="yachtEquipmentPacks.data"
            :page.sync="pagination.page"
            :sort-by.sync="sorting.sortBy"
            :sort-desc.sync="sorting.desc"
            :loading="yachtEquipmentPacks.loading"
            :server-items-length="yachtEquipmentPacks.meta.total"
            item-key="id"
            must-sort
            show-select
            hide-default-footer
            class="elevation-5"
          >
            <template v-slot:item="{ item: equipmentPack, isSelected, select }">
              <yacht-equipment-pack-row
                :yacht-equipment-pack="equipmentPack"
                :is-selected="isSelected"
                :select="select"
                @edit:yacht-equipment-pack="openYachtEquipmentPackEditDialog"
                @copy:yacht-equipment-pack="openYachtEquipmentPackCopyDialog"
                @view:yacht-equipment-pack="openYachtEquipmentPackViewDialog"
              />
            </template>
            <template slot="no-data">
              <!--<editor-fold desc="Filtered">-->
              <div
                v-if="isFiltered"
                class="text-center py-4"
              >
                <div>
                  There are no results for your search.
                </div>
                <div
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    @click="clearFilters"
                  >
                    <v-icon left>
                      clear
                    </v-icon>
                    Clear Filters
                  </v-btn>
                </div>
              </div>
              <!--</editor-fold>-->
              <!--<editor-fold desc="NoData">-->
              <div
                v-else-if="!yachtEquipmentPacks.loading"
                class="text-center py-4"
              >
                <v-col cols="16">
                  There are no equipment packs at the moment.
                </v-col>
                <v-col
                  v-if="$can('create', 'ServiceTask')"
                  cols="16"
                  class="mt-2"
                >
                  <v-btn
                    color="primary"
                    small
                    @click="openYachtEquipmentPackEditDialog()"
                  >
                    <v-icon left>
                      add
                    </v-icon>
                    Add New
                  </v-btn>
                </v-col>
              </div>
              <!--</editor-fold>-->
              <div
                v-else
                class="text-center py-4"
              >
                Loading...
              </div>
            </template>
          </v-data-table>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtEquipmentPacks.meta.from"
            :to="yachtEquipmentPacks.meta.to"
            :total="yachtEquipmentPacks.meta.total"
            :last-page="yachtEquipmentPacks.meta.last_page"
            :page.sync="pagination.page"
          />
          <!--</editor-fold>-->
        </div>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-equipment-pack-edit-dialog
      :yacht-equipment-pack="yachtEquipmentPackDirty"
      :visible.sync="yachtEquipmentPackEditDialog"
      @yachtEquipmentPack:created="yachtEquipmentPackCreated"
      @yachtEquipmentPack:updated="yachtEquipmentPackUpdated"
      @yachtEquipmentPack:deleted="yachtEquipmentPackDeleted"
    />

    <yacht-equipment-packs-import-dialog
      :visible.sync="massImportDialog"
      @imported="fetchYachtEquipmentPacks(); filtersDirty.status = 'draft'"
    />

    <yacht-equipment-pack-view-dialog
      v-if="equipmentPackViewDialog.id"
      :visible.sync="equipmentPackViewDialog.visible"
      :yacht-equipment-pack-id="equipmentPackViewDialog.id"
    />

    <yacht-equipment-pack-mass-edit-dialog
      v-if="selected.length > 0"
      :visible.sync="massEditDialog"
      :yacht-equipment-pack-ids="selected.map(p => p.id)"
      @edited="fetchYachtEquipmentPacks()"
    />
  </v-container>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import { mapGetters } from 'vuex'
import paginationMixin from '@/mixins/paginationMixin'
import YachtModel from '@/models/YachtModel'
import YachtEquipmentPackEditDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackEditDialog'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import YachtEquipmentPackRow from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackRow'
import filterableMixin from '@/mixins/filterableMixin'
import YachtModelSelect from '@/components/helpers/YachtModelSelect'
import YachtEquipmentPacksImportDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPacksImportDialog'
import YachtEquipmentPackViewDialog from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackViewDialog'
import YachtEquipmentPackMassEditDialog
  from '@/components/settings/yachtEquipmentPacks/YachtEquipmentPackMassEditDialog'

export default {
    name: 'YachtEquipmentPacksIndex',
    components: {
      YachtEquipmentPackMassEditDialog,
      YachtEquipmentPackViewDialog,
      YachtEquipmentPacksImportDialog,
      YachtModelSelect,
      YachtEquipmentPackRow,
      YachtEquipmentPackEditDialog
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachtEquipmentPacks: {
          data: [],
          loading: false,
          meta: {}
        },
        yachtModels: {
          data: [],
          loading: false
        },
        pagination: {
          perPage: 50
        },
        yachtEquipmentPackDirty: undefined,
        yachtEquipmentPackEditDialog: false,
        filtersClean: cloneDeep(YachtEquipmentPack.emptyFilters),
        filtersDirty: cloneDeep(YachtEquipmentPack.emptyFilters),
        selected: [],
        massImportDialog: false,
        equipmentPackViewDialog: {
          visible: false,
          id: undefined
        },
        massEditDialog: false
      }
    },
    computed: {
      allInstalledBys() {
        return Object.keys(YachtEquipmentPack.installedByData).map((key) => {
          return {
            text: YachtEquipmentPack.installedByData[key].label,
            value: key
          }
        })
      },
      allStatuses() {
        return Object.keys(YachtEquipmentPack.statusData).map((key) => {
          return {
            text: YachtEquipmentPack.statusData[key].label,
            value: key
          }
        })
      },
      countries() {
        return this.$store.getters['settings/charteringCountries']
      },
      categories() {
        return this.$store.getters['settings/equipmentPackCategories']
      },
      tableHeaders() {
        return [
          { text: 'Label', value: 'label' },
          { text: 'Installed by', value: 'installed_by', class: 'px-0' },
          { text: 'Models', value: 'models_count' },
          { text: 'Category', value: 'yacht_equipment_pack_category_id' },
          { text: 'Last Update', value: 'updated_at' },
          { text: 'Price', value: 'sale_price', class: 'text-right', align: 'end' },
          { sortable: false }
        ]
      },
      ...mapGetters('settings', ['contractors'])
    },
    created() {
      this.filtersDirty.status = YachtEquipmentPack.STATUS_ACTIVE

      this.fetchYachtEquipmentPacks()
      this.fetchYachtModels()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchYachtEquipmentPacks()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      fetchYachtEquipmentPacks: debounce(async function() {
        this.yachtEquipmentPacks.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          custom: false
        }

        try {
          let result = await YachtEquipmentPack.api.fetch(queryParams)

          this.yachtEquipmentPacks.data = result.data
          this.yachtEquipmentPacks.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtEquipmentPacks.loading = false
        }
      }, 500),
      async fetchYachtModels() {
        this.yachtModels.loading = true

        try {
          this.yachtModels.data = await YachtModel.api.fetch({
            status: YachtModel.STATUS_ACTIVE
          })
        } catch (e) {

        } finally {
          this.yachtModels.loading = false
        }
      },
      async openYachtEquipmentPackEditDialog(yachtEquipmentPack = undefined) {
        if (!yachtEquipmentPack) {
          this.yachtEquipmentPackDirty = undefined
          this.yachtEquipmentPackEditDialog = true
          return
        }

        try {
          this.yachtEquipmentPackDirty = await YachtEquipmentPack.api.find(yachtEquipmentPack.id)

          this.yachtEquipmentPackEditDialog = true
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        }
      },
      async openYachtEquipmentPackCopyDialog(yachtEquipmentPack = undefined) {
        try {
          this.yachtEquipmentPackDirty = await YachtEquipmentPack.api.find(yachtEquipmentPack.id)

          this.yachtEquipmentPackDirty.id = undefined
          this.yachtEquipmentPackDirty.copy = true
          this.yachtEquipmentPackEditDialog = true
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        }
      },
      openYachtEquipmentPackViewDialog(yachtEquipmentPack) {
        this.equipmentPackViewDialog.id = yachtEquipmentPack.id
        this.equipmentPackViewDialog.visible = true
      },
      clearFilters() {
        this.filters = cloneDeep(YachtEquipmentPack.emptyFilters)
      },
      yachtEquipmentPackCreated(yachtEquipmentPack) {
        this.yachtEquipmentPacks.data.unshift(yachtEquipmentPack)
      },
      yachtEquipmentPackUpdated(yachtEquipmentPack) {
        const index = this.yachtEquipmentPacks.data.findIndex(ep => ep.id === yachtEquipmentPack.id)

        this.yachtEquipmentPacks.data.splice(index, 1, yachtEquipmentPack)
      },
      yachtEquipmentPackDeleted(yachtEquipmentPackId) {
        const index = this.yachtEquipmentPacks.data.findIndex(ep => ep.id === yachtEquipmentPackId)

        this.yachtEquipmentPacks.data.splice(index, 1)
      }
    }
  }
</script>
